"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens, type UserRelationship } from "@/lib/api";

interface FollowButtonProps {
  username: string;
  // Optional: render compact (icon-only) variant for in-list use
  compact?: boolean;
  onChange?: (isFollowing: boolean, followerCount: number) => void;
}

/**
 * EPIC-006 STORY-603 — Follow / Unfollow button.
 *
 * Reads the relationship on mount, optimistic flip on click, rollback
 * on error. Hidden when the viewer is the same user as the target
 * (the page-level guard should also gate this — defense in depth).
 */
export function FollowButton({
  username,
  compact,
  onChange,
}: FollowButtonProps) {
  const { t } = useTranslation();
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [rel, setRel] = useState<UserRelationship | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthed(loadTokens() !== null);
  }, []);

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    void api
      .getUserRelationship(username)
      .then((r) => {
        if (!cancelled) setRel(r);
      })
      .catch(() => {
        // Silent fallback — button stays in loading state.
      });
    return () => {
      cancelled = true;
    };
  }, [authed, username]);

  if (!authed || !rel) return null;

  async function toggle() {
    if (pending || !rel) return;
    const wasFollowing = rel.isFollowing;
    const optimistic: UserRelationship = {
      ...rel,
      isFollowing: !wasFollowing,
      followerCount: rel.followerCount + (wasFollowing ? -1 : 1),
    };
    setRel(optimistic);
    setPending(true);
    try {
      if (wasFollowing) {
        await api.unfollowUser(username);
      } else {
        await api.followUser(username);
      }
      onChange?.(optimistic.isFollowing, optimistic.followerCount);
    } catch (err) {
      // Rollback.
      setRel(rel);
      // Best-effort error surfacing — caller can stack a real toast
      // system later. Closed-beta scope: window.alert is fine.
      window.alert(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : t("followButton.updateFailed"),
      );
    } finally {
      setPending(false);
    }
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={pending}
        aria-pressed={rel.isFollowing}
        aria-label={
          rel.isFollowing ? t("followButton.unfollow") : t("followButton.follow")
        }
        className="rounded-md border px-2 py-2 text-[11px] font-bold disabled:opacity-50 md:py-0.5"
        style={{
          borderColor: rel.isFollowing
            ? "var(--color-border)"
            : "var(--color-brand)",
          color: rel.isFollowing
            ? "var(--color-text3)"
            : "var(--color-brand)",
          background: rel.isFollowing
            ? "transparent"
            : "var(--color-brand-tint)",
        }}
      >
        {rel.isFollowing
          ? t("followButton.following")
          : t("followButton.followCta")}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      aria-pressed={rel.isFollowing}
      className="ml-auto rounded-md px-3 py-1.5 text-[12px] font-bold transition-colors disabled:opacity-50"
      style={{
        borderColor: rel.isFollowing
          ? "var(--color-border)"
          : "var(--color-brand)",
        color: rel.isFollowing ? "var(--color-text2)" : "white",
        background: rel.isFollowing
          ? "var(--color-bg)"
          : "var(--color-brand)",
        border: rel.isFollowing
          ? "1px solid var(--color-border)"
          : "1px solid var(--color-brand)",
      }}
    >
      {rel.isFollowing
        ? t("followButton.followingWithCount", { count: rel.followerCount })
        : t("followButton.followCtaWithCount", { count: rel.followerCount })}
    </button>
  );
}
